import { unzip } from 'fflate'
import { supabase } from '@/lib/integrations/supabase'
import type {
  DocumentBatch,
  DocumentBatchWithDocuments,
  DocumentUploadProgress,
  DocumentUploadOptions,
  DocumentUploadResult,
  StoredDocument,
} from '@/modules/documents/documentTypes'

const DOCUMENTS_BUCKET = 'documents'
const MAX_SOURCE_BYTES = 100 * 1024 * 1024
const MAX_XML_BYTES = 20 * 1024 * 1024
const MAX_TEXT_BYTES = 100 * 1024 * 1024
const MAX_UNCOMPRESSED_BYTES = 200 * 1024 * 1024
const MAX_ZIP_ENTRIES = 5000
const MAX_COMPRESSION_RATIO = 200

interface CandidateDocument {
  name: string
  bytes: Uint8Array
}

interface ZipEntryInfo {
  name: string
  compressedSize: number
  uncompressedSize: number
  encrypted: boolean
  compressionMethod: number
  isDirectory: boolean
}

function extensionOf(name: string) {
  return name.split('.').pop()?.toLowerCase() || ''
}

function safeFileName(name: string) {
  const normalized = name.replace(/\\/g, '/')
  const baseName = normalized.split('/').filter(Boolean).pop() || 'document.xml'
  return baseName.replace(/[\u0000-\u001f<>:"|?*]/g, '_').slice(0, 180)
}

function randomId() {
  return crypto.randomUUID()
}

function storagePath(unitId: string, category: 'batches' | 'documents', id: string, name: string) {
  return `${unitId}/${category}/${id}/${safeFileName(name)}`
}

async function sha256(bytes: Uint8Array) {
  const data = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer
  const digest = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(digest), (item) => item.toString(16).padStart(2, '0')).join('')
}

function decodeXml(bytes: Uint8Array) {
  const declarationBytes = bytes.subarray(0, Math.min(bytes.byteLength, 512))
  const declaration = new TextDecoder('ascii').decode(declarationBytes)
  const declaredEncoding = declaration.match(/<\?xml[^>]*encoding\s*=\s*["']([^"']+)["']/i)?.[1]?.toLowerCase()
  const encodingAliases: Record<string, string> = {
    'iso-8859-1': 'windows-1252',
    latin1: 'windows-1252',
    'utf-16': 'utf-16le',
  }
  const bomEncoding = bytes[0] === 0xff && bytes[1] === 0xfe
    ? 'utf-16le'
    : bytes[0] === 0xfe && bytes[1] === 0xff
      ? 'utf-16be'
      : null
  const encoding = bomEncoding || (declaredEncoding ? encodingAliases[declaredEncoding] || declaredEncoding : 'utf-8')

  try {
    return new TextDecoder(encoding, { fatal: false }).decode(bytes)
  } catch {
    throw new Error('xml_encoding_not_supported')
  }
}

function decodeStructuredText(bytes: Uint8Array) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return new TextDecoder('windows-1252', { fatal: false }).decode(bytes)
  }
}

function spedFields(line: string) {
  const fields = line.split('|')
  if (fields[0] === '') fields.shift()
  if (fields[fields.length - 1] === '') fields.pop()
  return fields
}

function inspectSped(bytes: Uint8Array) {
  const text = decodeStructuredText(bytes)
  const headerLine = text.split(/\r?\n/).find((line) => line.trim().startsWith('|0000|'))
  if (!headerLine) throw new Error('invalid_sped')

  const fields = spedFields(headerLine.trim())
  const isDate = (value: string | undefined) => /^\d{8}$/.test(value || '')
  const fiscal = isDate(fields[3]) && isDate(fields[4])
  const contributions = isDate(fields[5]) && isDate(fields[6])
  if (!fiscal && !contributions) throw new Error('invalid_sped')

  const documentKind = fiscal ? 'sped_fiscal' as const : 'sped_contributions' as const
  const periodStart = fiscal ? fields[3] : fields[5]
  const periodEnd = fiscal ? fields[4] : fields[6]
  const establishmentName = fiscal ? fields[5] : fields[7]
  const establishmentTaxId = (fiscal ? fields[6] : fields[8])?.replace(/\D/g, '') || null
  const isoDate = (value: string) => `${value.slice(4, 8)}-${value.slice(2, 4)}-${value.slice(0, 2)}`

  return {
    documentKind,
    metadata: {
      sped: {
        layout_key: documentKind,
        period_start: isoDate(periodStart),
        period_end: isoDate(periodEnd),
        establishment_name: establishmentName || null,
        establishment_tax_id: establishmentTaxId,
      },
    },
  }
}

function inspectXml(bytes: Uint8Array) {
  const text = decodeXml(bytes)
  if (/<!DOCTYPE/i.test(text.slice(0, 4096))) throw new Error('xml_doctype_not_allowed')
  const document = new DOMParser().parseFromString(text, 'application/xml')
  const parseError = document.querySelector('parsererror')

  if (parseError || !document.documentElement) {
    throw new Error('invalid_xml')
  }

  const root = document.documentElement
  const idAttribute = root.querySelector('[Id^="NFe"], [Id^="CTe"], [Id^="MDFe"]')?.getAttribute('Id')
  const accessKeyNode = root.querySelector('chNFe, chCTe, chMDFe')?.textContent?.trim()
  const externalKey = (idAttribute?.replace(/^(NFe|CTe|MDFe)/, '') || accessKeyNode || '').replace(/\D/g, '')

  const descendants = (element: Element) => Array.from(element.getElementsByTagName('*'))
  const firstElement = (element: Element, names: string[]) => {
    const candidates = descendants(element)
    for (const name of names) {
      const match = candidates.find((candidate) => candidate.localName.toLowerCase() === name.toLowerCase())
      if (match) return match
    }
    return null
  }
  const firstText = (element: Element, names: string[]) => firstElement(element, names)?.textContent?.trim() || null
  const issuer = firstElement(root, ['emit', 'prest', 'prestador'])
  const recipient = firstElement(root, ['dest', 'toma', 'tomador'])
  const itemElements = descendants(root).filter((element) => element.localName.toLowerCase() === 'det')
  const cfopCounts = new Map<string, number>()
  const cfopTotals = new Map<string, number>()
  for (const item of itemElements) {
    const cfop = firstText(item, ['CFOP'])
    if (cfop) {
      cfopCounts.set(cfop, (cfopCounts.get(cfop) || 0) + 1)
      const itemTotalText = firstText(item, ['vProd'])
      const itemTotal = itemTotalText === null ? null : Number(itemTotalText.replace(',', '.'))
      if (itemTotal !== null && Number.isFinite(itemTotal)) {
        cfopTotals.set(cfop, (cfopTotals.get(cfop) || 0) + itemTotal)
      }
    }
  }
  const totalText = firstText(root, ['vNF', 'vTPrest', 'vLiq', 'vServ'])
  const totalAmount = totalText === null ? null : Number(totalText.replace(',', '.'))
  if (cfopCounts.size === 0) {
    const documentCfop = firstText(root, ['CFOP'])
    if (documentCfop) {
      cfopCounts.set(documentCfop, 1)
      if (totalAmount !== null && Number.isFinite(totalAmount)) cfopTotals.set(documentCfop, totalAmount)
    }
  }

  return {
    externalKey: externalKey.length >= 40 ? externalKey : null,
    metadata: {
      xml_root: root.localName,
      xml_namespace: root.namespaceURI || null,
      fiscal_summary: {
        summary_version: 4,
        document_number: firstText(root, ['nNF', 'nCT', 'nNFSe', 'numero']),
        operation_type: firstText(root, ['tpNF']),
        issued_at: firstText(root, ['dhEmi', 'dEmi', 'dhProc', 'competencia']),
        supplier_name: issuer ? firstText(issuer, ['xNome', 'razaoSocial', 'nome']) : null,
        issuer_name: issuer ? firstText(issuer, ['xNome', 'razaoSocial', 'nome']) : null,
        issuer_tax_id: issuer ? firstText(issuer, ['CNPJ', 'CPF']) : null,
        recipient_name: recipient ? firstText(recipient, ['xNome', 'razaoSocial', 'nome']) : null,
        recipient_tax_id: recipient ? firstText(recipient, ['CNPJ', 'CPF']) : null,
        total_amount: Number.isFinite(totalAmount) ? totalAmount : null,
        item_count: itemElements.length,
        cfops: Array.from(cfopCounts, ([code, count]) => ({
          code,
          count,
          total_amount: cfopTotals.has(code) ? cfopTotals.get(code) : null,
        })),
      },
    },
  }
}

function findEndOfCentralDirectory(view: DataView) {
  const minimumOffset = Math.max(0, view.byteLength - 65557)

  for (let offset = view.byteLength - 22; offset >= minimumOffset; offset -= 1) {
    if (view.getUint32(offset, true) === 0x06054b50) return offset
  }

  throw new Error('invalid_zip')
}

function inspectZip(buffer: ArrayBuffer): ZipEntryInfo[] {
  const view = new DataView(buffer)
  const eocdOffset = findEndOfCentralDirectory(view)
  const entryCount = view.getUint16(eocdOffset + 10, true)
  const centralDirectoryOffset = view.getUint32(eocdOffset + 16, true)

  if (entryCount === 0xffff || centralDirectoryOffset === 0xffffffff) {
    throw new Error('zip64_not_supported')
  }
  if (entryCount > MAX_ZIP_ENTRIES) throw new Error('zip_too_many_entries')

  const decoder = new TextDecoder('utf-8', { fatal: false })
  const entries: ZipEntryInfo[] = []
  let offset = centralDirectoryOffset

  for (let index = 0; index < entryCount; index += 1) {
    if (offset + 46 > view.byteLength || view.getUint32(offset, true) !== 0x02014b50) {
      throw new Error('invalid_zip_directory')
    }

    const flags = view.getUint16(offset + 8, true)
    const compressionMethod = view.getUint16(offset + 10, true)
    const compressedSize = view.getUint32(offset + 20, true)
    const uncompressedSize = view.getUint32(offset + 24, true)
    const nameLength = view.getUint16(offset + 28, true)
    const extraLength = view.getUint16(offset + 30, true)
    const commentLength = view.getUint16(offset + 32, true)
    const nameStart = offset + 46
    const nameEnd = nameStart + nameLength

    if (nameEnd > view.byteLength) throw new Error('invalid_zip_directory')

    const name = decoder.decode(new Uint8Array(buffer, nameStart, nameLength))
    const normalizedName = name.replace(/\\/g, '/')

    if (
      normalizedName.includes('\u0000')
      || normalizedName.startsWith('/')
      || normalizedName.split('/').includes('..')
    ) {
      throw new Error('unsafe_zip_path')
    }

    entries.push({
      name,
      compressedSize,
      uncompressedSize,
      encrypted: Boolean(flags & 0x1),
      compressionMethod,
      isDirectory: normalizedName.endsWith('/'),
    })

    offset = nameEnd + extraLength + commentLength
  }

  const files = entries.filter((entry) => !entry.isDirectory)
  const uncompressedTotal = files.reduce((total, entry) => total + entry.uncompressedSize, 0)
  const compressedTotal = files.reduce((total, entry) => total + entry.compressedSize, 0)

  if (files.some((entry) => entry.encrypted)) throw new Error('encrypted_zip_not_supported')
  if (files.some((entry) => ![0, 8].includes(entry.compressionMethod))) throw new Error('zip_compression_not_supported')
  if (uncompressedTotal > MAX_UNCOMPRESSED_BYTES) throw new Error('zip_uncompressed_too_large')
  if (compressedTotal === 0 && uncompressedTotal > 0) throw new Error('unsafe_zip_ratio')
  if (compressedTotal > 0 && uncompressedTotal / compressedTotal > MAX_COMPRESSION_RATIO) {
    throw new Error('unsafe_zip_ratio')
  }
  if (files.some((entry) => (
    extensionOf(entry.name) === 'xml'
      ? entry.uncompressedSize > MAX_XML_BYTES
      : entry.uncompressedSize > MAX_TEXT_BYTES
  ))) throw new Error('source_file_too_large')

  return files
}

async function unzipSupportedFiles(file: File): Promise<{ candidates: CandidateDocument[]; rejectedNames: string[] }> {
  const buffer = await file.arrayBuffer()
  const entries = inspectZip(buffer)
  const supportedNames = new Set(entries
    .filter((entry) => ['xml', 'txt'].includes(extensionOf(entry.name)))
    .map((entry) => entry.name))
  const rejectedNames = entries
    .filter((entry) => !['xml', 'txt'].includes(extensionOf(entry.name)))
    .map((entry) => entry.name)

  if (supportedNames.size === 0) return { candidates: [], rejectedNames }

  const extracted = await new Promise<Record<string, Uint8Array>>((resolve, reject) => {
    unzip(new Uint8Array(buffer), (error, data) => {
      if (error) reject(error)
      else resolve(data)
    })
  })

  const candidates = Object.entries(extracted)
    .filter(([name]) => supportedNames.has(name))
    .map(([name, bytes]) => ({ name: safeFileName(name), bytes }))

  return { candidates, rejectedNames }
}

async function sourceCandidates(files: File[]) {
  if (files.length === 0) throw new Error('select_files')
  if (files.length > MAX_ZIP_ENTRIES) throw new Error('too_many_files')
  if (files.some((file) => file.size > MAX_SOURCE_BYTES)) throw new Error('source_file_too_large')

  const zipFiles = files.filter((file) => extensionOf(file.name) === 'zip')
  if (zipFiles.length > 0 && files.length > 1) throw new Error('zip_must_be_uploaded_alone')

  if (zipFiles.length === 1) {
    const result = await unzipSupportedFiles(zipFiles[0])
    return {
      sourceType: 'zip' as const,
      candidates: result.candidates,
      rejectedNames: result.rejectedNames,
      zipFile: zipFiles[0],
    }
  }

  const rejectedNames = files
    .filter((file) => !['xml', 'txt'].includes(extensionOf(file.name)))
    .map((file) => file.name)
  const supportedFiles = files.filter((file) => ['xml', 'txt'].includes(extensionOf(file.name)))
  const candidates = await Promise.all(supportedFiles.map(async (file) => {
    if (extensionOf(file.name) === 'xml' && file.size > MAX_XML_BYTES) throw new Error('xml_too_large')
    if (extensionOf(file.name) === 'txt' && file.size > MAX_TEXT_BYTES) throw new Error('source_file_too_large')
    return { name: safeFileName(file.name), bytes: new Uint8Array(await file.arrayBuffer()) }
  }))

  return {
    sourceType: supportedFiles.every((file) => extensionOf(file.name) === 'xml')
      ? 'xml' as const
      : supportedFiles.every((file) => extensionOf(file.name) === 'txt')
        ? 'text' as const
        : 'mixed' as const,
    candidates,
    rejectedNames,
    zipFile: null,
  }
}

async function updateBatch(batchId: string, values: Record<string, unknown>) {
  const { data, error } = await supabase
    .from('document_batches')
    .update(values)
    .eq('id', batchId)
    .select()
    .single()

  if (error) throw error
  return data as DocumentBatch
}

async function uploadOneDocument(
  unitId: string,
  batchId: string,
  candidate: CandidateDocument,
  options?: DocumentUploadOptions,
) {
  const hash = await sha256(candidate.bytes)
  const { data: duplicate, error: duplicateError } = await supabase
    .from('documents')
    .select('id')
    .eq('unit_id', unitId)
    .eq('content_sha256', hash)
    .maybeSingle()

  if (duplicateError) throw duplicateError
  if (duplicate) return 'duplicate' as const

  const isXml = extensionOf(candidate.name) === 'xml'
  const inspected = isXml
    ? { documentKind: 'xml' as const, ...inspectXml(candidate.bytes) }
    : inspectSped(candidate.bytes)
  const documentKind = inspected.documentKind
  const mimeType = isXml ? 'application/xml' : 'text/plain'
  const documentId = randomId()
  const path = storagePath(unitId, 'documents', documentId, candidate.name)
  const payload = candidate.bytes.buffer.slice(
    candidate.bytes.byteOffset,
    candidate.bytes.byteOffset + candidate.bytes.byteLength,
  ) as ArrayBuffer
  const blob = new Blob([payload], { type: mimeType })

  const { error: uploadError } = await supabase.storage.from(DOCUMENTS_BUCKET).upload(path, blob, {
    contentType: mimeType,
    upsert: false,
  })
  if (uploadError) throw uploadError

  const { error: insertError } = await supabase.from('documents').insert({
    id: documentId,
    unit_id: unitId,
    batch_id: batchId,
    document_kind: documentKind,
    status: 'valid',
    original_name: candidate.name,
    storage_path: path,
    mime_type: mimeType,
    size_bytes: candidate.bytes.byteLength,
    content_sha256: hash,
    external_key: 'externalKey' in inspected ? inspected.externalKey : null,
    metadata: {
      ...inspected.metadata,
      ...(options?.purpose ? { upload_purpose: options.purpose } : {}),
    },
  })

  if (insertError) {
    await supabase.storage.from(DOCUMENTS_BUCKET).remove([path])
    if (insertError.code === '23505') return 'duplicate' as const
    throw insertError
  }

  return 'accepted' as const
}

export async function uploadDocuments(
  unitId: string,
  files: File[],
  onProgress?: (progress: DocumentUploadProgress) => void,
  options?: DocumentUploadOptions,
): Promise<DocumentUploadResult> {
  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!authData.user) throw new Error('authentication_required')

  const extensions = new Set(files.map((file) => extensionOf(file.name)))
  const sourceType = extensions.has('zip')
    ? 'zip'
    : extensions.size === 1 && extensions.has('xml')
      ? 'xml'
      : extensions.size === 1 && extensions.has('txt')
        ? 'text'
        : 'mixed'
  const originalName = files.length === 1 ? files[0].name : `${files.length} arquivos`
  const { data: createdBatch, error: batchError } = await supabase
    .from('document_batches')
    .insert({
      unit_id: unitId,
      source_type: sourceType,
      status: 'processing',
      original_name: originalName,
      total_size_bytes: files.reduce((total, file) => total + file.size, 0),
      metadata: options?.purpose ? { purpose: options.purpose } : {},
    })
    .select()
    .single()

  if (batchError) throw batchError
  const batch = createdBatch as DocumentBatch

  try {
    const source = await sourceCandidates(files)
    let originalStoragePath: string | null = null

    if (source.zipFile) {
      originalStoragePath = storagePath(unitId, 'batches', batch.id, source.zipFile.name)
      const { error: zipUploadError } = await supabase.storage
        .from(DOCUMENTS_BUCKET)
        .upload(originalStoragePath, source.zipFile, {
          contentType: source.zipFile.type || 'application/zip',
          upsert: false,
        })
      if (zipUploadError) throw zipUploadError
    }

    let accepted = 0
    let duplicates = 0
    let rejected = source.rejectedNames.length
    const rejectionDetails = source.rejectedNames.map((name) => ({ name: safeFileName(name), reason: 'unsupported_file' }))

    for (let index = 0; index < source.candidates.length; index += 1) {
      const candidate = source.candidates[index]
      onProgress?.({ current: index + 1, total: source.candidates.length, fileName: candidate.name })

      try {
        const result = await uploadOneDocument(unitId, batch.id, candidate, options)
        if (result === 'accepted') accepted += 1
        else duplicates += 1
      } catch (error) {
        rejected += 1
        rejectionDetails.push({
          name: candidate.name,
          reason: error instanceof Error ? error.message : 'document_upload_failed',
        })
      }
    }

    const fileCount = source.candidates.length + source.rejectedNames.length
    const completedBatch = await updateBatch(batch.id, {
      status: rejected > 0 ? 'completed_with_errors' : 'completed',
      original_storage_path: originalStoragePath,
      file_count: fileCount,
      accepted_count: accepted,
      duplicate_count: duplicates,
      rejected_count: rejected,
      metadata: {
        ...(options?.purpose ? { purpose: options.purpose } : {}),
        rejections: rejectionDetails.slice(0, 100),
      },
      completed_at: new Date().toISOString(),
    })

    return { batch: completedBatch, accepted, duplicates, rejected }
  } catch (error) {
    await updateBatch(batch.id, {
      status: 'failed',
      error_message: error instanceof Error ? error.message : 'batch_processing_failed',
      completed_at: new Date().toISOString(),
    }).catch(() => undefined)
    throw error
  }
}

export async function listDocumentBatches(unitId: string): Promise<DocumentBatchWithDocuments[]> {
  const { data, error } = await supabase
    .from('document_batches')
    .select('*, documents(*)')
    .eq('unit_id', unitId)
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data || []) as DocumentBatchWithDocuments[]
}

type FiscalSummaryDocument = Pick<
  StoredDocument,
  'id' | 'document_kind' | 'storage_path' | 'metadata'
>

export async function hydrateDocumentsFiscalSummaries(
  documents: FiscalSummaryDocument[],
  onSummary?: (documentId: string, summary: Record<string, unknown>) => void,
): Promise<Map<string, Record<string, unknown>>> {
  const pending = documents.filter((document) => (
    document.document_kind === 'xml'
    && Boolean(document.storage_path)
    && (
      !document.metadata?.fiscal_summary
      || (document.metadata.fiscal_summary as Record<string, unknown>).summary_version !== 4
    )
  ))

  if (pending.length === 0) return new Map()

  const summaries = new Map<string, Record<string, unknown>>()
  let cursor = 0
  const worker = async () => {
    while (cursor < pending.length) {
      const document = pending[cursor]
      cursor += 1
      try {
        const signedUrl = await getDocumentDownloadUrl(document.storage_path!)
        const response = await fetch(signedUrl)
        if (!response.ok) continue
        const inspected = inspectXml(new Uint8Array(await response.arrayBuffer()))
        const summary = inspected.metadata.fiscal_summary as Record<string, unknown>

        const { error } = await supabase.rpc('set_document_fiscal_summary', {
          p_document_id: document.id,
          p_summary: summary,
        })
        if (error) throw error

        summaries.set(document.id, summary)
        onSummary?.(document.id, summary)
      } catch (error) {
        console.warn('[hydrateDocumentFiscalSummaries] Could not persist document summary:', document.id, error)
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(4, pending.length) }, () => worker()))
  return summaries
}

export async function hydrateDocumentFiscalSummaries(
  batches: DocumentBatchWithDocuments[],
  onSummary?: (documentId: string, summary: Record<string, unknown>) => void,
): Promise<DocumentBatchWithDocuments[]> {
  const summaries = await hydrateDocumentsFiscalSummaries(
    batches.flatMap((batch) => batch.documents || []),
    onSummary,
  )

  if (summaries.size === 0) return batches

  return batches.map((batch) => ({
    ...batch,
    documents: (batch.documents || []).map((document) => {
      const summary = summaries.get(document.id)
      return summary ? {
        ...document,
        metadata: { ...document.metadata, fiscal_summary: summary },
      } : document
    }),
  }))
}

export async function listOrphanDocuments(unitId: string): Promise<StoredDocument[]> {
  const { data, error } = await supabase
    .from('documents')
    .select('*')
    .eq('unit_id', unitId)
    .is('batch_id', null)
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data || []) as StoredDocument[]
}

export async function getDocumentDownloadUrl(storagePathValue: string) {
  const { data, error } = await supabase.storage
    .from(DOCUMENTS_BUCKET)
    .createSignedUrl(storagePathValue, 300)

  if (error) throw error
  return data.signedUrl
}

export async function deleteDocumentBatch(batchId: string): Promise<void> {
  const { data, error: loadError } = await supabase
    .from('document_batches')
    .select('id, original_storage_path, documents(id, storage_path, status)')
    .eq('id', batchId)
    .single()

  if (loadError) throw loadError

  const batch = data as {
    id: string
    original_storage_path: string | null
    documents: Array<{
      id: string
      storage_path: string | null
      status: string
    }>
  }

  if ((batch.documents || []).some((document) => ['processed', 'purged'].includes(document.status))) {
    throw new Error('document_already_booked')
  }
  const paths = Array.from(new Set([
    batch.original_storage_path,
    ...(batch.documents || []).map((document) => document.storage_path),
  ].filter((path): path is string => Boolean(path))))

  // Delete physical files first. If Storage refuses any path, database records
  // remain intact and the user can retry without creating broken references.
  if (paths.length > 0) {
    const { error: storageError } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .remove(paths)

    if (storageError) throw storageError
  }

  // Delete child records explicitly as well as relying on ON DELETE CASCADE.
  // This keeps deletion correct in databases that applied the early migration
  // version with ON DELETE SET NULL and have not run migration 094 yet.
  const { error: documentsDeleteError } = await supabase
    .from('documents')
    .delete()
    .eq('batch_id', batchId)

  if (documentsDeleteError) throw documentsDeleteError

  const { error: deleteError } = await supabase
    .from('document_batches')
    .delete()
    .eq('id', batchId)

  if (deleteError) throw deleteError
}

export async function deleteOrphanDocuments(unitId: string): Promise<number> {
  const documents = await listOrphanDocuments(unitId)
  if (documents.length === 0) return 0

  if (documents.some((document) => ['processed', 'purged'].includes(document.status))) {
    throw new Error('document_already_booked')
  }

  const paths = Array.from(new Set(
    documents
      .map((document) => document.storage_path)
      .filter((path): path is string => Boolean(path)),
  ))

  if (paths.length > 0) {
    const { error: storageError } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .remove(paths)

    if (storageError) throw storageError
  }

  const { error: deleteError } = await supabase
    .from('documents')
    .delete()
    .eq('unit_id', unitId)
    .is('batch_id', null)

  if (deleteError) throw deleteError
  return documents.length
}

export async function purgeDocumentBatchFiles(batchId: string): Promise<number> {
  const { data: authData, error: authError } = await supabase.auth.getUser()
  if (authError) throw authError
  if (!authData.user) throw new Error('authentication_required')

  const { data, error: loadError } = await supabase
    .from('document_batches')
    .select('id, original_storage_path, documents(id, storage_path, status)')
    .eq('id', batchId)
    .single()

  if (loadError) throw loadError

  const batch = data as {
    id: string
    original_storage_path: string | null
    documents: Array<{
      id: string
      storage_path: string | null
      status: string
    }>
  }
  const documents = batch.documents || []

  if (documents.some((document) => !['processed', 'purged'].includes(document.status))) {
    throw new Error('document_not_booked')
  }

  const paths = Array.from(new Set([
    batch.original_storage_path,
    ...documents.map((document) => document.storage_path),
  ].filter((path): path is string => Boolean(path))))

  if (paths.length > 0) {
    const { error: storageError } = await supabase.storage
      .from(DOCUMENTS_BUCKET)
      .remove(paths)
    if (storageError) throw storageError
  }

  if (documents.length > 0) {
    const { error: documentsError } = await supabase
      .from('documents')
      .update({
        storage_path: null,
        status: 'purged',
        purged_at: new Date().toISOString(),
        purged_by: authData.user.id,
      })
      .in('id', documents.map((document) => document.id))

    if (documentsError) throw documentsError
  }

  const { error: batchError } = await supabase
    .from('document_batches')
    .update({
      original_storage_path: null,
      status: 'purged',
    })
    .eq('id', batchId)

  if (batchError) throw batchError
  return paths.length
}

export type { DocumentBatch, DocumentBatchWithDocuments, StoredDocument }
